import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  CUSTOMIZABLE_TOKEN_KEYS,
  isCustomizableTokenKey,
  isThemeOverrideKey,
  isThemeFontId,
  isThemeHeadingFontId,
  isThemePresetId,
  type ThemeFontId,
  type ThemeHeadingFontId,
  type ThemePresetId,
} from './theme/theme-presets'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  resolvedTheme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void

  /** Active preset id, or null when no preset is selected (= Fairgen defaults). */
  presetId: ThemePresetId | null
  setPresetId: (id: ThemePresetId | null) => void

  /** Active body font family id, or null when no font is selected (= Geist). */
  fontFamily: ThemeFontId | null
  setFontFamily: (id: ThemeFontId | null) => void

  /**
   * Active heading font family id, or null when no heading font is selected
   * (= Sumana, the Fairgen default).
   */
  headingFontFamily: ThemeHeadingFontId | null
  setHeadingFontFamily: (id: ThemeHeadingFontId | null) => void

  /** Per-org token overrides applied via inline `style.setProperty`. */
  themeOverrides: Record<string, string>
  setThemeOverrides: (overrides: Record<string, string>) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Holds only an explicit user choice. Renamed from `app-theme` (which was
// auto-written on first load) to reset everyone to their system setting once.
const STORAGE_KEY = 'app-theme-choice'
const LEGACY_STORAGE_KEY = 'app-theme'
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'
const PRESET_STORAGE_KEY = 'theme-preset'
const FONT_STORAGE_KEY = 'theme-font'
const HEADING_FONT_STORAGE_KEY = 'theme-heading-font'
const OVERRIDES_STORAGE_KEY = 'theme-overrides'

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY)
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {}
  return null
}

function getSystemDarkQuery(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null
  }
  return window.matchMedia(SYSTEM_DARK_QUERY)
}

function getSystemTheme(): Theme | null {
  const query = getSystemDarkQuery()
  if (!query) {
    return null
  }
  return query.matches ? 'dark' : 'light'
}

function getStoredPreset(): ThemePresetId | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(PRESET_STORAGE_KEY)
    return isThemePresetId(stored) ? stored : null
  } catch {
    return null
  }
}

function getStoredFont(): ThemeFontId | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(FONT_STORAGE_KEY)
    return isThemeFontId(stored) ? stored : null
  } catch {
    return null
  }
}

function getStoredHeadingFont(): ThemeHeadingFontId | null {
  if (typeof window === 'undefined') return null
  try {
    const stored = localStorage.getItem(HEADING_FONT_STORAGE_KEY)
    return isThemeHeadingFontId(stored) ? stored : null
  } catch {
    return null
  }
}

function getStoredOverrides(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  try {
    const stored = localStorage.getItem(OVERRIDES_STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored)
    if (!parsed || typeof parsed !== 'object') return {}
    const sanitized: Record<string, string> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (isThemeOverrideKey(key) && typeof value === 'string') {
        sanitized[key] = value
      }
    }
    return sanitized
  } catch {
    return {}
  }
}

/** Tokens that ThemeProvider has actively written via `style.setProperty` and may need to clear. */
function clearInlineTokens(tokens: Array<string>) {
  const style = document.documentElement.style
  for (const token of tokens) {
    style.removeProperty(token)
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'dark',
}: {
  children: ReactNode
  defaultTheme?: Theme
}) {
  const [initialStoredTheme] = useState(getStoredTheme)
  // Without an explicit choice the theme follows the system setting and is not persisted.
  const [hasChoice, setHasChoice] = useState(initialStoredTheme !== null)
  const [theme, setThemeState] = useState<Theme>(
    () => initialStoredTheme ?? getSystemTheme() ?? defaultTheme,
  )
  const [presetId, setPresetIdState] = useState<ThemePresetId | null>(() => getStoredPreset())
  const [fontFamily, setFontFamilyState] = useState<ThemeFontId | null>(() => getStoredFont())
  const [headingFontFamily, setHeadingFontFamilyState] = useState<ThemeHeadingFontId | null>(
    () => getStoredHeadingFont(),
  )
  const [themeOverrides, setThemeOverridesState] = useState<Record<string, string>>(() =>
    getStoredOverrides(),
  )

  useLayoutEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    root.classList.toggle('light', theme === 'light')
    if (!hasChoice) {
      return
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {}
  }, [theme, hasChoice])

  useEffect(() => {
    if (hasChoice) {
      return
    }
    const query = getSystemDarkQuery()
    if (!query) {
      return
    }
    const onSystemChange = (event: MediaQueryListEvent) => {
      setThemeState(event.matches ? 'dark' : 'light')
    }
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onSystemChange)
      return () => query.removeEventListener('change', onSystemChange)
    }
    query.addListener(onSystemChange)
    return () => query.removeListener(onSystemChange)
  }, [hasChoice])

  useLayoutEffect(() => {
    const root = document.documentElement
    if (presetId && isThemePresetId(presetId)) {
      root.setAttribute('data-theme-preset', presetId)
    } else {
      root.removeAttribute('data-theme-preset')
    }
    try {
      if (presetId) {
        localStorage.setItem(PRESET_STORAGE_KEY, presetId)
      } else {
        localStorage.removeItem(PRESET_STORAGE_KEY)
      }
    } catch {}
  }, [presetId])

  useLayoutEffect(() => {
    const root = document.documentElement
    if (fontFamily && isThemeFontId(fontFamily)) {
      root.setAttribute('data-font', fontFamily)
    } else {
      root.removeAttribute('data-font')
    }
    try {
      if (fontFamily) {
        localStorage.setItem(FONT_STORAGE_KEY, fontFamily)
      } else {
        localStorage.removeItem(FONT_STORAGE_KEY)
      }
    } catch {}
  }, [fontFamily])

  useLayoutEffect(() => {
    const root = document.documentElement
    // null or 'sumana' = Fairgen default — remove the attribute so globals.css default applies.
    if (headingFontFamily && isThemeHeadingFontId(headingFontFamily) && headingFontFamily !== 'sumana') {
      root.setAttribute('data-heading-font', headingFontFamily)
    } else {
      root.removeAttribute('data-heading-font')
    }
    try {
      if (headingFontFamily && headingFontFamily !== 'sumana') {
        localStorage.setItem(HEADING_FONT_STORAGE_KEY, headingFontFamily)
      } else {
        localStorage.removeItem(HEADING_FONT_STORAGE_KEY)
      }
    } catch {}
  }, [headingFontFamily])

  useLayoutEffect(() => {
    const style = document.documentElement.style
    const mode = theme // 'light' | 'dark'

    // Build a map of base-token → resolved value, preferring the mode-specific
    // key ("--background:dark") over the generic base key ("--background").
    const resolved: Record<string, string> = {}
    for (const [key, value] of Object.entries(themeOverrides)) {
      if (!isThemeOverrideKey(key) || typeof value !== 'string' || value.length === 0) continue
      const colonIdx = key.lastIndexOf(':')
      if (colonIdx === -1) {
        // Base key — only apply if no mode-specific key overrides it.
        if (!(`${key}:${mode}` in themeOverrides)) {
          resolved[key] = value
        }
      } else {
        const base = key.slice(0, colonIdx)
        const keyMode = key.slice(colonIdx + 1)
        if (keyMode === mode) {
          resolved[base] = value
        }
      }
    }

    // Clear all customizable tokens then re-apply the resolved set.
    clearInlineTokens([...CUSTOMIZABLE_TOKEN_KEYS])

    for (const [key, value] of Object.entries(resolved)) {
      style.setProperty(key, value)
    }

    try {
      const hasKeys = Object.keys(themeOverrides).length > 0
      if (hasKeys) {
        localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(themeOverrides))
      } else {
        localStorage.removeItem(OVERRIDES_STORAGE_KEY)
      }
    } catch {}
  }, [themeOverrides, theme])

  const setTheme = useCallback((t: Theme) => {
    setHasChoice(true)
    setThemeState(t)
  }, [])
  const toggleTheme = useCallback(() => {
    setHasChoice(true)
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }, [])

  const setPresetId = useCallback((id: ThemePresetId | null) => {
    setPresetIdState(id && isThemePresetId(id) ? id : null)
  }, [])

  const setFontFamily = useCallback((id: ThemeFontId | null) => {
    setFontFamilyState(id && isThemeFontId(id) ? id : null)
  }, [])

  const setHeadingFontFamily = useCallback((id: ThemeHeadingFontId | null) => {
    setHeadingFontFamilyState(id && isThemeHeadingFontId(id) ? id : null)
  }, [])

  const setThemeOverrides = useCallback((overrides: Record<string, string>) => {
    const sanitized: Record<string, string> = {}
    if (overrides && typeof overrides === 'object') {
      for (const [key, value] of Object.entries(overrides)) {
        if (isThemeOverrideKey(key) && typeof value === 'string' && value.length > 0) {
          sanitized[key] = value
        }
      }
    }
    setThemeOverridesState(sanitized)
  }, [])

  const value = useMemo<ThemeContextType>(
    () => ({
      theme,
      resolvedTheme: theme,
      setTheme,
      toggleTheme,
      presetId,
      setPresetId,
      fontFamily,
      setFontFamily,
      headingFontFamily,
      setHeadingFontFamily,
      themeOverrides,
      setThemeOverrides,
    }),
    [
      theme,
      setTheme,
      toggleTheme,
      presetId,
      setPresetId,
      fontFamily,
      setFontFamily,
      headingFontFamily,
      setHeadingFontFamily,
      themeOverrides,
      setThemeOverrides,
    ],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
